import { useEffect, useRef, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowPathIcon, CubeIcon } from '@heroicons/react/24/outline';

import { Alert, Button, Spinner } from '@shared';

import {
  applyConstructionInfoMatrices,
  parseConstructionInfoMatrices,
} from '@/components/stl-construction-info';
import { StlViewer } from '@/components/StlViewer';
import {
  getSupportedViewerFileFormat,
  getStlFileTypeConfig,
  getStlLayerOverride,
  isStlSupportedViewerFile,
  STL_LAYOUT_CONFIG,
  type StlLayoutConfig,
  type StlMatrix4,
} from '@/components/stl-layout';
import { errorKeyOf, type ApiErrorKey } from '@/services/api/errors';
import { OrdersApi } from '@/services/api/orders-api';
import { useTranslations } from '@/i18n/client';
import type { PortalClient } from '@/services/api/portal-client';
import type { ProjectOrderDetails, ProjectOrderScanFile } from '@/services/api/types';
import { parseUtcDate } from '@/utils/date-time';

import { OrderReviewFilesPanel } from './OrderReviewSections';
import type { OrderReviewCopy } from './order-review-types';

interface OrderReviewWorkspaceProps {
  order: ProjectOrderDetails;
  client: PortalClient;
  copy: Pick<OrderReviewCopy, 'viewer' | 'files'>;
  modelFilePolicy: 'configured-models' | 'all-supported-3d';
}

interface ViewerColors {
  background: string | null;
  model: string | null;
  scan: string | null;
  aestheticPlate: string | null;
}

interface ConstructionMatrixSection {
  key: string;
  label: string;
  matrix: StlMatrix4;
}

type ViewerRotationDegrees = [number, number, number];

const DEFAULT_VIEWER_ROTATION: ViewerRotationDegrees = [0, 0, 0];

function getConstructionMatrixSections(
  config: StlLayoutConfig,
  labels: {
    scene: string;
    jaw: Record<'upper' | 'lower' | 'other', string>;
    file: (fileName: string) => string;
  },
): ConstructionMatrixSection[] {
  const sections: ConstructionMatrixSection[] = [];

  if (config.hasConstructionInfoSceneMatrix) {
    sections.push({ key: 'scene', label: labels.scene, matrix: config.sceneMatrix });
  }

  for (const jaw of ['upper', 'lower', 'other'] as const) {
    const jawConfig = config.jaws[jaw];
    if (!jawConfig.hasConstructionInfoMatrix) {
      continue;
    }

    const hasEquivalentFileOverride = config.layerOverrides.some(
      (override) =>
        override.jaw === jaw && override.matrix.every((value, index) => value === jawConfig.matrix[index]),
    );
    if (hasEquivalentFileOverride) {
      continue;
    }

    sections.push({ key: `jaw:${jaw}`, label: labels.jaw[jaw], matrix: jawConfig.matrix });
  }

  config.layerOverrides.forEach((override, index) => {
    sections.push({
      key: `override:${index}`,
      label: labels.file(override.fileName),
      matrix: override.matrix,
    });
  });

  return sections;
}

function updateConstructionMatrixCell(
  config: StlLayoutConfig,
  sectionKey: string,
  cellIndex: number,
  value: number,
): StlLayoutConfig {
  const updatedMatrix = (matrix: StlMatrix4): StlMatrix4 => {
    const next = [...matrix] as StlMatrix4;
    next[cellIndex] = value;
    return next;
  };

  if (sectionKey === 'scene') {
    return { ...config, sceneMatrix: updatedMatrix(config.sceneMatrix) };
  }

  if (sectionKey.startsWith('jaw:')) {
    const jaw = sectionKey.slice(4) as keyof StlLayoutConfig['jaws'];
    return {
      ...config,
      jaws: {
        ...config.jaws,
        [jaw]: { ...config.jaws[jaw], matrix: updatedMatrix(config.jaws[jaw].matrix) },
      },
    };
  }

  if (sectionKey.startsWith('override:')) {
    const overrideIndex = Number(sectionKey.slice('override:'.length));
    return {
      ...config,
      layerOverrides: config.layerOverrides.map((override, index) =>
        index === overrideIndex ? { ...override, matrix: updatedMatrix(override.matrix) } : override,
      ),
    };
  }

  return config;
}

function isReviewModelFile(
  file: ProjectOrderScanFile,
  modelFilePolicy: OrderReviewWorkspaceProps['modelFilePolicy'],
  config: StlLayoutConfig,
): boolean {
  if (modelFilePolicy === 'configured-models') {
    return isStlSupportedViewerFile(file.name, config) && !file.isAdditionalFile && !file.isProtocolFile;
  }

  return getSupportedViewerFileFormat(file.name) !== undefined && !file.isProtocolFile;
}

function isCadModelFile(file: ProjectOrderScanFile): boolean {
  const format = getSupportedViewerFileFormat(file.name);
  return format !== undefined && file.name.trim().toLocaleLowerCase('de').endsWith(`_cad.${format}`);
}

function sortFilesByUploadDate(files: readonly ProjectOrderScanFile[]): ProjectOrderScanFile[] {
  return [...files].sort((left, right) => {
    const leftTimestamp = parseUtcDate(left.uploadedAtUtc).getTime();
    const rightTimestamp = parseUtcDate(right.uploadedAtUtc).getTime();
    const leftIsValid = Number.isFinite(leftTimestamp);
    const rightIsValid = Number.isFinite(rightTimestamp);

    if (leftIsValid !== rightIsValid) {
      return leftIsValid ? -1 : 1;
    }

    if (!leftIsValid || !rightIsValid) {
      return 0;
    }

    return rightTimestamp - leftTimestamp;
  });
}

function defaultReviewFiles(
  files: readonly ProjectOrderScanFile[],
  config: StlLayoutConfig,
): ProjectOrderScanFile[] {
  const cadFiles = files.filter(isCadModelFile);
  if (cadFiles.length > 0) {
    return cadFiles;
  }

  return files.filter(
    (file) =>
      (getStlLayerOverride(file.name, config)?.color ?? getStlFileTypeConfig(file.name)?.color) === 'model',
  );
}

function isConstructionInfoFile(file: ProjectOrderScanFile): boolean {
  const name = file.name.trim().replaceAll('\\', '/').split('/').at(-1)?.toLowerCase() ?? '';
  return (
    name.endsWith('.constructioninfo') ||
    name.endsWith('.constructioninfo.xml') ||
    name === 'constructioninfo.xml'
  );
}

function readThemeColor(variableName: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim() || null;
}

function ViewerEmptyState({
  title,
  description,
  overlay = false,
}: {
  title: string;
  description: string;
  overlay?: boolean;
}) {
  return (
    <div
      className={`grid h-full min-h-[20rem] place-items-center content-center gap-[0.7rem] p-8 text-center${
        overlay ? ' pointer-events-none absolute inset-0' : ''
      }`}
      role="img"
      aria-labelledby="order-review-viewer-title"
      aria-describedby="order-review-viewer-description"
    >
      <CubeIcon className="h-12 w-12 text-[var(--md-highlight)]" aria-hidden="true" />
      <strong className="text-[var(--md-viewer-text)]">{title}</strong>
      <p className="m-0 max-w-[42ch] text-[var(--md-viewer-text)]" id="order-review-viewer-description">
        {description}
      </p>
    </div>
  );
}

export function OrderReviewWorkspace({ order, client, copy, modelFilePolicy }: OrderReviewWorkspaceProps) {
  const t = useTranslations('common.designReview');
  const tErrors = useTranslations('errors.api');
  const queryClient = useQueryClient();

  const [downloadErrorKey, setDownloadErrorKey] = useState<ApiErrorKey | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [visibleFileIds, setVisibleFileIds] = useState<string[]>([]);
  const [viewerOpacities, setViewerOpacities] = useState<Record<string, number>>({});
  const [applyConstructionInfoMatrix, setApplyConstructionInfoMatrix] = useState(true);
  const [isConstructionMatrixEditorExpanded, setIsConstructionMatrixEditorExpanded] = useState(false);
  const [constructionMatrixCellValues, setConstructionMatrixCellValues] = useState<Record<string, string>>(
    {},
  );
  const [viewerColors, setViewerColors] = useState<ViewerColors>(() => ({
    background: readThemeColor('--md-primary'),
    model: readThemeColor('--md-tooth-color'),
    scan: readThemeColor('--md-gum-color'),
    aestheticPlate: readThemeColor('--md-aestheticplate-color'),
  }));
  const [viewerRotationDegrees, setViewerRotationDegrees] =
    useState<ViewerRotationDegrees>(DEFAULT_VIEWER_ROTATION);
  const autoDisplayedOrderId = useRef<string | null>(null);

  const constructionInfoFile = (order.scanFiles ?? []).find(isConstructionInfoFile);
  const constructionInfoFileId = constructionInfoFile?.id?.trim() ?? '';
  const constructionInfoQuery = useQuery<StlLayoutConfig | null>({
    queryKey: ['mdenture', 'order-construction-info', order.id, constructionInfoFileId] as const,
    enabled: Boolean(constructionInfoFileId),
    queryFn: async ({ signal }) => {
      if (!constructionInfoFileId) {
        throw new Error('Missing authenticated construction info context.');
      }

      const blob = await new OrdersApi(client).downloadOrderFile(
        { orderId: order.id, fileId: constructionInfoFileId },
        signal,
      );
      const matrices = parseConstructionInfoMatrices(await blob.text());
      return matrices.length > 0 ? applyConstructionInfoMatrices(STL_LAYOUT_CONFIG, matrices) : null;
    },
  });
  const reviewFiles = sortFilesByUploadDate(
    (order.scanFiles ?? []).filter((file) =>
      isReviewModelFile(file, modelFilePolicy, constructionInfoQuery.data ?? STL_LAYOUT_CONFIG),
    ),
  );
  const viewerQueries = useQueries({
    queries: reviewFiles.map((file) => {
      const fileId = file.id?.trim() ?? '';

      return {
        queryKey: ['mdenture', 'order-file', order.id, fileId] as const,
        enabled: false,
        queryFn: ({ signal }: { signal: AbortSignal }) => {
          if (!fileId) {
            throw new Error('Missing authenticated viewer context.');
          }

          return new OrdersApi(client).downloadOrderFile({ orderId: order.id, fileId }, signal);
        },
      };
    }),
  });

  async function handleDownload(file: ProjectOrderScanFile): Promise<void> {
    const fileId = file.id?.trim();
    if (!fileId || downloadingFileId) {
      return;
    }

    setDownloadErrorKey(null);
    setDownloadingFileId(fileId);

    try {
      const blob = await new OrdersApi(client).downloadOrderFile({ orderId: order.id, fileId });
      const downloadUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = file.name;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
    } catch (error) {
      setDownloadErrorKey(errorKeyOf(error));
    } finally {
      setDownloadingFileId(null);
    }
  }

  function prefetchViewerFile(fileId: string): void {
    if (!fileId) {
      return;
    }

    const queryKey = ['mdenture', 'order-file', order.id, fileId] as const;
    if (queryClient.getQueryData<Blob>(queryKey)) {
      return;
    }

    void queryClient
      .fetchQuery({
        queryKey,
        queryFn: ({ signal }) =>
          new OrdersApi(client).downloadOrderFile({ orderId: order.id, fileId }, signal),
      })
      .catch(() => undefined);
  }

  function toggleFileVisibility(file: ProjectOrderScanFile): void {
    const fileId = file.id?.trim();
    if (!fileId) {
      return;
    }

    if (!visibleFileIds.includes(fileId)) {
      prefetchViewerFile(fileId);
    }

    setVisibleFileIds((current) =>
      current.includes(fileId) ? current.filter((id) => id !== fileId) : [...current, fileId],
    );
  }

  function showAllFiles(): void {
    reviewFiles.forEach((file) => {
      const fileId = file.id?.trim();
      if (fileId) {
        prefetchViewerFile(fileId);
      }
    });

    setVisibleFileIds(
      reviewFiles.flatMap((file) => {
        const fileId = file.id?.trim();
        return fileId ? [fileId] : [];
      }),
    );
  }

  function handleViewerColorChange(color: keyof ViewerColors, value: string): void {
    setViewerColors((current) => ({ ...current, [color]: value }));
  }

  function handleViewerRotationChange(axis: 0 | 1 | 2, value: string): void {
    const degrees = Number(value);
    if (!Number.isFinite(degrees)) {
      return;
    }

    setViewerRotationDegrees((current) => {
      const next: ViewerRotationDegrees = [...current];
      next[axis] = degrees;
      return next;
    });
  }

  function resetViewerRotation(): void {
    setViewerRotationDegrees(DEFAULT_VIEWER_ROTATION);
  }

  function handleViewerOpacityChange(fileId: string, value: string): void {
    const percentage = Number(value);
    if (!fileId || !Number.isFinite(percentage)) {
      return;
    }

    setViewerOpacities((current) => ({
      ...current,
      [fileId]: Math.min(100, Math.max(0, percentage)),
    }));
  }

  useEffect(() => {
    if (constructionInfoFileId && !constructionInfoQuery.isFetched) {
      return;
    }

    if (autoDisplayedOrderId.current === order.id) {
      return;
    }

    autoDisplayedOrderId.current = order.id;
    const defaultFiles = defaultReviewFiles(reviewFiles, constructionInfoQuery.data ?? STL_LAYOUT_CONFIG);
    const defaultFileIds = defaultFiles.flatMap((file) => {
      const fileId = file.id?.trim();
      return fileId ? [fileId] : [];
    });

    setVisibleFileIds(defaultFileIds);
    defaultFileIds.forEach((fileId) => {
      const queryKey = ['mdenture', 'order-file', order.id, fileId] as const;
      if (queryClient.getQueryData<Blob>(queryKey)) {
        return;
      }

      void queryClient
        .fetchQuery({
          queryKey,
          queryFn: ({ signal }) =>
            new OrdersApi(client).downloadOrderFile({ orderId: order.id, fileId }, signal),
        })
        .catch(() => undefined);
    });
  }, [
    client,
    constructionInfoFileId,
    constructionInfoQuery.data,
    constructionInfoQuery.isFetched,
    order.id,
    queryClient,
    reviewFiles,
  ]);

  const viewerBackgroundColor = viewerColors.background ?? '';
  const viewerModelColor = viewerColors.model ?? '';
  const viewerScanColor = viewerColors.scan ?? '';
  const viewerAestheticPlateColor = viewerColors.aestheticPlate ?? '';
  const hasViewerColors =
    viewerBackgroundColor !== '' &&
    viewerModelColor !== '' &&
    viewerScanColor !== '' &&
    viewerAestheticPlateColor !== '';
  const modelRotation: [number, number, number] = [
    viewerRotationDegrees[0],
    viewerRotationDegrees[1],
    viewerRotationDegrees[2],
  ].map((degrees) => (degrees * Math.PI) / 180) as [number, number, number];
  const hasViewerRotation = viewerRotationDegrees.some(
    (degrees, index) => degrees !== DEFAULT_VIEWER_ROTATION[index],
  );
  const rotationAxes = [
    { index: 0 as const, label: t('viewer.rotationX') },
    { index: 1 as const, label: t('viewer.rotationY') },
    { index: 2 as const, label: t('viewer.rotationZ') },
  ];
  const visibleReviewFileIndices = reviewFiles.flatMap((file, index) => {
    const fileId = file.id?.trim();
    return fileId && visibleFileIds.includes(fileId) ? [index] : [];
  });
  const loadingFileIds = new Set(
    reviewFiles.flatMap((file, index) => {
      const fileId = file.id?.trim();
      const query = viewerQueries[index];
      return fileId && query?.isFetching && !query.data ? [fileId] : [];
    }),
  );
  const viewerLayers = reviewFiles.flatMap((file, index) => {
    const fileId = file.id?.trim();
    const blob = viewerQueries[index]?.data;
    const color = getStlLayerOverride(file.name, constructionInfoQuery.data ?? STL_LAYOUT_CONFIG)?.color;
    return fileId && blob ? [{ id: fileId, name: file.name, file: blob, ...(color ? { color } : {}) }] : [];
  });
  const viewerLayerOpacities = Object.fromEntries(
    Object.entries(viewerOpacities).map(([fileId, percentage]) => [fileId, percentage / 100]),
  );
  const viewerQueryError = visibleReviewFileIndices
    .map((index) => viewerQueries[index])
    .find((query) => query?.isError)?.error;
  const isViewerLoading = visibleReviewFileIndices.some(
    (index) => viewerQueries[index]?.isFetching && !viewerQueries[index]?.data,
  );
  const hasVisibleViewerFiles = viewerLayers.some((layer) => visibleFileIds.includes(layer.id));
  const allFilesVisible =
    reviewFiles.length > 0 &&
    reviewFiles.every((file) => {
      const fileId = file.id?.trim();
      return Boolean(fileId && visibleFileIds.includes(fileId));
    });
  const sourceConstructionConfig = constructionInfoQuery.data;
  const constructionMatrixSections = sourceConstructionConfig
    ? getConstructionMatrixSections(sourceConstructionConfig, {
        scene: t('viewer.matrixScene'),
        jaw: {
          upper: t('viewer.matrixJaw.upper'),
          lower: t('viewer.matrixJaw.lower'),
          other: t('viewer.matrixJaw.other'),
        },
        file: (fileName) => t('viewer.matrixFile', { fileName }),
      })
    : [];
  const editedConstructionConfig = sourceConstructionConfig
    ? Object.entries(constructionMatrixCellValues).reduce((config, [cellKey, rawValue]) => {
        const separatorIndex = cellKey.lastIndexOf('.');
        const sectionKey = cellKey.slice(0, separatorIndex);
        const cellIndex = Number(cellKey.slice(separatorIndex + 1));
        const parsedValue = Number(rawValue.trim().replace(',', '.'));
        return rawValue.trim() && Number.isInteger(cellIndex) && Number.isFinite(parsedValue)
          ? updateConstructionMatrixCell(config, sectionKey, cellIndex, parsedValue)
          : config;
      }, sourceConstructionConfig)
    : null;
  const viewerLayoutConfig = applyConstructionInfoMatrix ? editedConstructionConfig : null;

  return (
    <>
      <section className="md-review__panel order-2 flex min-w-0 flex-col lg:order-none lg:col-start-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="md-review__section-eyebrow">{t('viewer.eyebrow')}</span>
            <h2 className="mb-[0.35rem] text-[1.2rem]" id="order-review-viewer-title">
              {copy.viewer.title}
            </h2>
          </div>
          <CubeIcon
            className="h-[1.45rem] w-[1.45rem] shrink-0 text-[var(--md-highlight)]"
            aria-hidden="true"
          />
        </div>
        <fieldset className="mb-3 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg)] px-3 py-2.5">
          <legend className="sr-only">{t('viewer.rotationControls')}</legend>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-bold tracking-[0.04em] text-[var(--zs-muted)] uppercase">
              {t('viewer.rotationControls')}
            </span>
            <Button
              type="button"
              variant="secondary"
              className="h-9 w-9 min-w-[2.25rem] shrink-0 p-0"
              disabled={!hasViewerRotation}
              onClick={resetViewerRotation}
              aria-label={t('viewer.rotationReset')}
              title={t('viewer.rotationReset')}
            >
              <ArrowPathIcon className="h-[1.15rem] w-[1.15rem] shrink-0" aria-hidden="true" />
            </Button>
          </div>
          <div className="mt-2 grid gap-3 sm:grid-cols-3">
            {rotationAxes.map(({ index, label }) => (
              <label
                key={label}
                className="grid min-w-0 gap-1.5 text-sm font-semibold text-[var(--md-primary)]"
              >
                <span className="flex items-center justify-between gap-2">
                  <span>{label}</span>
                  <output className="font-mono text-xs text-[var(--zs-muted)]">
                    {viewerRotationDegrees[index]}°
                  </output>
                </span>
                <input
                  className="w-full accent-[var(--md-highlight)]"
                  type="range"
                  min="-180"
                  max="180"
                  step="1"
                  value={viewerRotationDegrees[index]}
                  onChange={(event) => handleViewerRotationChange(index, event.target.value)}
                  aria-label={label}
                />
              </label>
            ))}
          </div>
        </fieldset>
        {import.meta.env.DEV && sourceConstructionConfig ? (
          <div className="mb-3 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg)] px-3 py-2.5">
            <label className="flex items-center gap-2 text-sm font-semibold text-[var(--md-primary)]">
              <input
                className="h-4 w-4 accent-[var(--md-highlight)]"
                type="checkbox"
                checked={applyConstructionInfoMatrix}
                onChange={(event) => setApplyConstructionInfoMatrix(event.target.checked)}
              />
              <span>{t('viewer.applyConstructionInfoMatrix')}</span>
            </label>
            <button
              className="mt-2 text-sm font-semibold text-[var(--md-highlight)] underline underline-offset-2"
              type="button"
              aria-expanded={isConstructionMatrixEditorExpanded}
              aria-controls="construction-info-matrix-editor"
              onClick={() => setIsConstructionMatrixEditorExpanded((expanded) => !expanded)}
            >
              {t('viewer.matrixEditor')}
            </button>
            {isConstructionMatrixEditorExpanded ? (
              <div id="construction-info-matrix-editor" className="mt-3 grid gap-3">
                {constructionMatrixSections.map((section) => (
                  <fieldset
                    className="min-w-0 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] p-3"
                    key={section.key}
                  >
                    <legend className="max-w-full px-1 text-xs font-bold text-[var(--md-primary)] [overflow-wrap:anywhere]">
                      {section.label}
                    </legend>
                    <div className="grid grid-cols-4 gap-1.5">
                      {Array.from({ length: 16 }, (_, displayIndex) => {
                        const cellIndex = (displayIndex % 4) * 4 + Math.floor(displayIndex / 4);
                        const cellKey = `${section.key}.${cellIndex}`;
                        const row = Math.floor(displayIndex / 4) + 1;
                        const column = (displayIndex % 4) + 1;
                        const axisNames = [
                          t('viewer.matrixAxis.x'),
                          t('viewer.matrixAxis.y'),
                          t('viewer.matrixAxis.z'),
                          t('viewer.matrixAxis.w'),
                        ];
                        const outputAxis = axisNames[row - 1] ?? '';
                        const inputAxis = axisNames[column - 1] ?? '';
                        const tooltipDescription =
                          row === 4 && column === 4
                            ? t('viewer.matrixCellHomogeneousScale')
                            : row === 4
                              ? t('viewer.matrixCellHomogeneous', { inputAxis })
                              : column === 4
                                ? t('viewer.matrixCellTranslation', { outputAxis })
                                : t('viewer.matrixCellContribution', { inputAxis, outputAxis });
                        const tooltip = t('viewer.matrixCellTooltip', {
                          row,
                          column,
                          description: tooltipDescription,
                        });
                        return (
                          <label
                            className="grid min-w-0 gap-0.5 text-center text-[0.625rem] font-semibold text-[var(--zs-muted)]"
                            key={cellKey}
                            title={tooltip}
                          >
                            <span aria-hidden="true">
                              M{row}
                              {column}
                            </span>
                            <input
                              className="h-9 min-w-0 rounded border border-[var(--md-border)] bg-[var(--md-white)] px-1 text-center font-mono text-xs text-[var(--md-primary)]"
                              type="text"
                              inputMode="decimal"
                              value={
                                constructionMatrixCellValues[cellKey] ?? String(section.matrix[cellIndex])
                              }
                              aria-label={t('viewer.matrixCell', { row, column })}
                              title={tooltip}
                              onChange={(event) =>
                                setConstructionMatrixCellValues((current) => ({
                                  ...current,
                                  [cellKey]: event.target.value,
                                }))
                              }
                            />
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        <div
          className="relative min-h-[27rem] flex-1 overflow-hidden rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg-soft)] text-[var(--md-primary)] max-sm:min-h-[20rem]"
          style={viewerBackgroundColor ? { backgroundColor: viewerBackgroundColor } : undefined}
        >
          {isViewerLoading && viewerLayers.length === 0 ? (
            <div className="md-review__viewer-loading grid h-full min-h-[20rem] place-items-center p-8 text-[var(--md-viewer-text)]">
              <Spinner label={t('viewer.loading')} />
            </div>
          ) : viewerQueryError && viewerLayers.length === 0 ? (
            <div className="grid h-full min-h-[20rem] place-items-center p-8 text-center">
              <Alert variant="error">{tErrors(errorKeyOf(viewerQueryError))}</Alert>
            </div>
          ) : viewerLayers.length > 0 ? (
            <>
              <StlViewer
                files={viewerLayers}
                ariaLabel={`${copy.viewer.title}: ${reviewFiles
                  .filter((file) => {
                    const fileId = file.id?.trim();
                    return Boolean(fileId && visibleFileIds.includes(fileId));
                  })
                  .map((file) => file.name)
                  .join(', ')}`}
                loadingLabel={t('viewer.loading')}
                errorLabel={t('viewer.parseError')}
                {...(viewerBackgroundColor ? { backgroundColor: viewerBackgroundColor } : {})}
                {...(viewerModelColor ? { modelColor: viewerModelColor } : {})}
                {...(viewerScanColor ? { scanColor: viewerScanColor } : {})}
                {...(viewerAestheticPlateColor ? { aestheticPlateColor: viewerAestheticPlateColor } : {})}
                modelRotation={modelRotation}
                layerOpacities={viewerLayerOpacities}
                visibleLayerIds={visibleFileIds}
                {...(viewerLayoutConfig ? { layoutConfig: viewerLayoutConfig } : {})}
              />
              {isViewerLoading ? (
                <div className="md-review__viewer-loading pointer-events-none absolute inset-0 grid min-h-[20rem] place-items-center p-8 text-[var(--md-viewer-text)]">
                  <Spinner label={t('viewer.loading')} />
                </div>
              ) : viewerQueryError ? (
                <div className="pointer-events-none absolute inset-0 grid min-h-[20rem] place-items-center p-8 text-center">
                  <Alert variant="error">{tErrors(errorKeyOf(viewerQueryError))}</Alert>
                </div>
              ) : !hasVisibleViewerFiles ? (
                <ViewerEmptyState
                  title={copy.viewer.placeholder}
                  description={copy.viewer.description}
                  overlay
                />
              ) : null}
            </>
          ) : (
            <ViewerEmptyState title={copy.viewer.placeholder} description={copy.viewer.description} />
          )}
        </div>
        {hasViewerColors ? (
          <fieldset className="mt-3 grid grid-cols-1 items-start justify-items-start gap-x-6 gap-y-2 rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-bg)] px-3 py-2.5 sm:grid-cols-2">
            <legend className="sr-only">{t('viewer.colorControls')}</legend>
            <span className="col-span-full w-full text-xs font-bold tracking-[0.04em] text-[var(--zs-muted)] uppercase sm:col-span-2">
              {t('viewer.colorControls')}
            </span>
            <label className="flex min-w-0 items-center justify-start gap-2 text-sm font-semibold text-[var(--md-primary)]">
              <input
                className="h-9 w-12 cursor-pointer rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-white)] p-1"
                type="color"
                value={viewerBackgroundColor}
                onChange={(event) => handleViewerColorChange('background', event.target.value)}
                aria-label={t('viewer.backgroundColor')}
              />
              <span className="[overflow-wrap:anywhere]">{t('viewer.backgroundColor')}</span>
            </label>
            <label className="flex min-w-0 items-center justify-start gap-2 text-sm font-semibold text-[var(--md-primary)]">
              <input
                className="h-9 w-12 cursor-pointer rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-white)] p-1"
                type="color"
                value={viewerModelColor}
                onChange={(event) => handleViewerColorChange('model', event.target.value)}
                aria-label={t('viewer.modelColor')}
              />
              <span className="[overflow-wrap:anywhere]">{t('viewer.modelColor')}</span>
            </label>
            <label className="flex min-w-0 items-center justify-start gap-2 text-sm font-semibold text-[var(--md-primary)]">
              <input
                className="h-9 w-12 cursor-pointer rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-white)] p-1"
                type="color"
                value={viewerScanColor}
                onChange={(event) => handleViewerColorChange('scan', event.target.value)}
                aria-label={t('viewer.scanColor')}
              />
              <span className="[overflow-wrap:anywhere]">{t('viewer.scanColor')}</span>
            </label>
            <label className="flex min-w-0 items-center justify-start gap-2 text-sm font-semibold text-[var(--md-primary)]">
              <input
                className="h-9 w-12 cursor-pointer rounded-[var(--zs-radius-s)] border border-[var(--md-border)] bg-[var(--md-white)] p-1"
                type="color"
                value={viewerAestheticPlateColor}
                onChange={(event) => handleViewerColorChange('aestheticPlate', event.target.value)}
                aria-label={t('viewer.aestheticPlateColor')}
              />
              <span className="[overflow-wrap:anywhere]">{t('viewer.aestheticPlateColor')}</span>
            </label>
          </fieldset>
        ) : null}
      </section>

      <OrderReviewFilesPanel
        files={reviewFiles}
        labels={{
          eyebrow: copy.files.eyebrow,
          title: copy.files.title,
          description: copy.files.description,
          count: t('files.count', { count: reviewFiles.length }),
          layerControls: t('files.layerControls'),
          showAll: t('files.showAll'),
          hideAll: t('files.hideAll'),
          opacity: t('files.opacity'),
          hideAction: t('files.hideAction'),
          viewAction: t('files.viewAction'),
          downloadAction: t('files.downloadAction'),
          downloadBusy: t('files.downloadBusy'),
          viewerLoading: t('viewer.loading'),
          noFiles: copy.files.noFiles,
          reviewHint: copy.files.reviewHint,
        }}
        visibleFileIds={visibleFileIds}
        loadingFileIds={loadingFileIds}
        viewerOpacities={viewerOpacities}
        downloadingFileId={downloadingFileId}
        allFilesVisible={allFilesVisible}
        downloadError={downloadErrorKey ? tErrors(downloadErrorKey) : null}
        onShowAll={showAllFiles}
        onHideAll={() => setVisibleFileIds([])}
        onToggleVisibility={toggleFileVisibility}
        onDownload={(file) => void handleDownload(file)}
        onOpacityChange={handleViewerOpacityChange}
      />
    </>
  );
}
